import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EventModal = ({ isOpen, onClose, event, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    start_date: '',
    end_date: '',
    event_type: 'other',
    description: '',
    audience: 'all'
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        event_type: event.event_type || 'other',
        description: event.description || '',
        audience: event.audience || 'all'
      });
    } else {
      setFormData({
        title: '',
        start_date: '',
        end_date: '',
        event_type: 'other',
        description: '',
        audience: 'all'
      });
    }
    setErrors({});
  }, [event, isOpen]);

  const eventTypes = [
    { value: 'reporting', label: 'Reporting / Return to Campus', icon: 'UserPlus' },
    { value: 'registration', label: 'Unit Registration', icon: 'BookOpen' },
    { value: 'add_drop', label: 'Add/Drop Period', icon: 'RefreshCw' },
    { value: 'assessment', label: 'Assessment / CATs', icon: 'ClipboardCheck' },
    { value: 'fee_due', label: 'Fee Deadline', icon: 'CreditCard' },
    { value: 'exam', label: 'Examinations', icon: 'FileCheck' },
    { value: 'results', label: 'Results Release', icon: 'Award' },
    { value: 'supplementary_exam', label: 'Supplementary Exams', icon: 'FileWarning' },
    { value: 'clearance', label: 'Clearance & Graduation', icon: 'GraduationCap' },
    { value: 'holiday', label: 'Holiday', icon: 'Calendar' },
    { value: 'other', label: 'Other Event', icon: 'Info' }
  ];

  const validate = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Event title is required';
    if (!formData.start_date) newErrors.start_date = 'Start date is required';
    if (!formData.end_date) newErrors.end_date = 'End date is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    if (formData.start_date && formData.end_date) {
      if (new Date(formData.start_date) > new Date(formData.end_date)) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    try {
      await onSave(formData);
      // onClose handled by parent
    } catch (error) {
      console.error('Error saving event:', error);
      setErrors({ submit: 'Failed to save event. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-card border border-border rounded-lg max-w-2xl w-full shadow-lg">
        <div className="bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {event ? 'Edit Event' : 'Add New Event'}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Event Title *
              </label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Semester 1 Reporting"
                className={errors.title ? 'border-error' : ''}
              />
              {errors.title && (
                <p className="text-sm text-error mt-1">{errors.title}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md bg-input text-foreground ${errors.start_date ? 'border-error' : 'border-border'
                    }`}
                />
                {errors.start_date && (
                  <p className="text-sm text-error mt-1">{errors.start_date}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  End Date *
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md bg-input text-foreground ${errors.end_date ? 'border-error' : 'border-border'
                    }`}
                />
                {errors.end_date && (
                  <p className="text-sm text-error mt-1">{errors.end_date}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Event Type *
                </label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground outline-none focus:ring-2 focus:ring-primary"
                >
                  {eventTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Target Audience
                </label>
                <select
                  value={formData.audience}
                  onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">Everyone</option>
                  <option value="students">Students Only</option>
                  <option value="staff">Staff Only</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Details regarding this event..."
                className={`w-full px-3 py-2 border rounded-md bg-input text-foreground ${errors.description ? 'border-error' : 'border-border'
                  }`}
              />
              {errors.description && (
                <p className="text-sm text-error mt-1">{errors.description}</p>
              )}
            </div>
          </div>

          {/* Event Type Preview */}
          <div className="bg-muted/50 rounded-lg p-4 border border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon
                  name={eventTypes.find(t => t.value === formData.event_type)?.icon || 'Calendar'}
                  size={20}
                  className="text-primary"
                />
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground">
                  {formData.title || 'New Event'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {eventTypes.find(t => t.value === formData.event_type)?.label} • {formData.start_date || 'Start'} to {formData.end_date || 'End'}
                </p>
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="bg-error/10 border border-error rounded-lg p-3">
              <p className="text-sm text-error">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  {event ? 'Update Event' : 'Add Event'}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventModal;